import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {TierOrPlan} from '../utils/tier';
import Cookies from 'js-cookie';
import {USER_SELECTED_ORG_UID} from '../constants/auth';
import {getLocalAccessToken, logout} from '../actions/auth';
import {getUserProfile} from '../actions/userProfile';

export interface OrganizationTier {
    name: string;
    description: string;
    type: TierOrPlan;
    expiry: number;
    perks: Array<{
        description: string;
    }>;
    tier_mgmt?: {
        text: string;
        url?: string;
    };
}

export interface OrganizationAlias {
    name: string;
}

export interface OrganizationInterface {
    name: string;
    uid: string;
    tier?: OrganizationTier;
    enabled_features: Array<string>;
    alias: OrganizationAlias;
}

export interface ReferralCodeInterface {
    code: string;
    expiry: string;
}

// ideally, should map as-is to the /user/profile endpoint
interface UserProfileInterfaceBase {
    username: string;
    email: string;
    is_verified: boolean;
    first_name: string;
    last_name: string;
    org_admins: OrganizationInterface[];
    org_viewers: OrganizationInterface[];
    my_organizations: OrganizationInterface[];
    referral_code: ReferralCodeInterface;
    referral_count: number;
    date_joined: string; // Added this line
}

// custom decorators
export interface UserProfileInterface extends UserProfileInterfaceBase {
    enabled_experiments: Array<String>;
    currentOrganization: OrganizationInterface;
}

interface UserProfileStateInterface extends UserProfileInterface {
    isLoading: boolean;
    error: any;
}

const DefaultState: UserProfileStateInterface = {
    username: '',
    email: '',
    is_verified: false,
    first_name: '',
    last_name: '',
    org_admins: [],
    org_viewers: [],
    my_organizations: [],
    isLoading: true,
    error: null,
    referral_code: {
        code: '',
        expiry: '',
    },
    referral_count: 0,
    enabled_experiments: [],
    currentOrganization: {
        name: '',
        uid: '',
        enabled_features: [],
        alias: {
            name: '',
        },
    },
    date_joined: '', // Added this line
};

const UserProfileSlice = createSlice({
    name: 'userProfile',
    initialState: DefaultState,
    reducers: {
        setCurrentOrganization: (state, action: PayloadAction<OrganizationInterface>) => {
            state.currentOrganization = action.payload;
            Cookies.set(USER_SELECTED_ORG_UID, action.payload.uid);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getUserProfile.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getUserProfile.fulfilled, (state, action: PayloadAction<UserProfileInterfaceBase>) => {
                state.isLoading = false;
                state.error = null;

                const {
                    username,
                    email,
                    first_name,
                    last_name,
                    my_organizations,
                    org_admins,
                    org_viewers,
                    is_verified,
                    referral_code,
                    referral_count,
                    date_joined,
                    ...rest
                } = action.payload;

                state.username = username;
                state.email = email;
                state.first_name = first_name;
                state.last_name = last_name;
                // NOTE: for backwards compatibility, org_admins is prefixed with my_organizations
                state.org_admins = org_admins;
                state.org_viewers = org_viewers;
                state.my_organizations = my_organizations;
                state.is_verified = is_verified;
                state.referral_code = referral_code;
                state.referral_count = referral_count;
                state.date_joined = date_joined;

                state.enabled_experiments = [];

                const userSelectedOrgUid = Cookies.get(USER_SELECTED_ORG_UID);
                const userSelectedOrg = userSelectedOrgUid
                    ? [...my_organizations, ...org_admins, ...org_viewers].find((org) => org.uid === userSelectedOrgUid)
                    : undefined;

                state.currentOrganization = userSelectedOrg || my_organizations[0];
            })
            .addCase(getUserProfile.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message ?? 'Unknown Error';
            });

        builder.addCase(getLocalAccessToken.pending, (state, action) => {
            // can do something
            const {username, email, first_name, last_name, my_organizations} = DefaultState;
            state.username = username;
            state.email = email;
            state.first_name = first_name;
            state.last_name = last_name;
            state.org_admins = my_organizations;
        });

        builder.addCase(logout.fulfilled, (state, action) => {
            const {username, email, first_name, last_name, my_organizations} = DefaultState;
            state.username = username;
            state.email = email;
            state.first_name = first_name;
            state.last_name = last_name;
            state.org_admins = my_organizations;
        });
    },
});

const {setCurrentOrganization} = UserProfileSlice.actions;

export {setCurrentOrganization};

export default UserProfileSlice.reducer;
